export function isTaskJob(job) {
    return String(job?.job_type || "").toLowerCase() === "task";
}

function compareCleaningJobs(leftJob, rightJob) {
    const leftHasB2BNew = leftJob.tags && leftJob.tags.includes("B2B_NEW");
    const rightHasB2BNew = rightJob.tags && rightJob.tags.includes("B2B_NEW");

    if (leftHasB2BNew && !rightHasB2BNew) {
        return -1;
    }
    if (!leftHasB2BNew && rightHasB2BNew) {
        return 1;
    }

    if (!leftJob.full_address) {
        return 1;
    }
    if (!rightJob.full_address) {
        return -1;
    }
    return leftJob.full_address.localeCompare(rightJob.full_address);
}

function compareTaskIdsDescending(leftId, rightId) {
    const leftNumericId = Number(leftId);
    const rightNumericId = Number(rightId);
    if (!Number.isNaN(leftNumericId) && !Number.isNaN(rightNumericId)) {
        return rightNumericId - leftNumericId;
    }
    return String(rightId || "").localeCompare(String(leftId || ""));
}

export function sortTaskJobs(jobs = []) {
    return jobs.slice().sort((leftJob, rightJob) => {
        const leftCreatedAt = leftJob.created_at || "";
        const rightCreatedAt = rightJob.created_at || "";

        if (leftCreatedAt && rightCreatedAt && leftCreatedAt !== rightCreatedAt) {
            return rightCreatedAt.localeCompare(leftCreatedAt);
        }
        if (leftCreatedAt && !rightCreatedAt) {
            return -1;
        }
        if (!leftCreatedAt && rightCreatedAt) {
            return 1;
        }
        return compareTaskIdsDescending(leftJob.cos_job_id, rightJob.cos_job_id);
    });
}

export function stringToDate(dateString) {
    const parts = dateString.split("-");
    return new Date(parts[0], parts[1] - 1, parts[2]);
}

export function sortSchedulerJobs(jobs = []) {
    const taskJobs = [];
    const cleaningJobs = [];

    jobs.forEach((job) => {
        if (isTaskJob(job)) {
            taskJobs.push(job);
        } else {
            cleaningJobs.push(job);
        }
    });

    return [
        ...sortTaskJobs(taskJobs),
        ...cleaningJobs.slice().sort(compareCleaningJobs),
    ];
}

export function mergeSchedulerJobsById(...jobGroups) {
    const jobsById = new Map();

    jobGroups.forEach((jobs) => {
        jobs.forEach((job) => {
            jobsById.set(String(job.cos_job_id), job);
        });
    });

    return Array.from(jobsById.values());
}

export function mapItemsById(items = []) {
    return items.reduce((map, item) => {
        map[item.id] = item.name;
        return map;
    }, {});
}

export function buildStatusCounts(jobs = [], statusKey) {
    return jobs.reduce((counts, job) => {
        const key = statusKey ? statusKey(job) : job.status;
        counts[key] = counts[key] ? counts[key] + 1 : 1;
        return counts;
    }, {});
}

export function buildFieldCounts(jobs = [], fieldName) {
    return jobs.reduce((counts, job) => {
        if (job.status === "Cancelled") {
            return counts;
        }

        counts[job[fieldName]] = counts[job[fieldName]] ? counts[job[fieldName]] + 1 : 1;
        return counts;
    }, {});
}

export function buildWorkerCounts(jobs = []) {
    return buildFieldCounts(jobs, "cos_job_worker");
}

export function buildWorkerWorkloadCounts(jobs = []) {
    return jobs.reduce((counts, job) => {
        if (job.status === "Cancelled" || job.cos_job_worker == null || job.cos_job_worker === "") {
            return counts;
        }

        const workerId = String(job.cos_job_worker);
        const workerCounts = counts[workerId] || {
            incompleteCleaning: 0,
            completedCleaning: 0,
            tasks: 0,
        };

        if (isTaskJob(job)) {
            workerCounts.tasks += 1;
        } else if (job.status === "ReadyForBilling") {
            workerCounts.completedCleaning += 1;
        } else {
            workerCounts.incompleteCleaning += 1;
        }

        counts[workerId] = workerCounts;
        return counts;
    }, {});
}

export function buildSummaryCountMap(summaryCounts = []) {
    return summaryCounts.reduce((map, count) => {
        map[count.cos_job_id] = count;
        return map;
    }, {});
}

export function enrichJobsWithSummaryCounts(jobs = [], summaryCountMap = {}) {
    return jobs.map((job) => ({
        ...job,
        notes_count: summaryCountMap[job.cos_job_id]?.notes_count ?? 0,
        images_count: summaryCountMap[job.cos_job_id]?.images_count ?? 0,
        req_fix_count: summaryCountMap[job.cos_job_id]?.req_fix_count ?? 0,
        req_deep_clean_count: summaryCountMap[job.cos_job_id]?.req_deep_clean_count ?? 0,
        req_key_count: summaryCountMap[job.cos_job_id]?.req_key_count ?? 0,
    }));
}

export function jobMatchesSearch(job, searchText, fieldWorkerMap, fieldSupervisorMap) {
    if (!searchText) {
        return true;
    }

    const normalizedSearch = searchText.toLowerCase();
    const values = [
        job.full_address,
        job.agent_name,
        job.cos_customer_name,
        job.phone,
        job.email,
        job.cos_job_id,
        fieldWorkerMap[job.cos_job_worker],
        fieldSupervisorMap[job.cos_job_supervisor],
        job.booking_source?.code,
        job.booking_source?.label,
    ];

    return values.some((value) => value && String(value).toLowerCase().includes(normalizedSearch));
}

export function buildStatusCountsWithTask(jobs = []) {
    const counts = buildStatusCounts(jobs);
    counts.Task = jobs.filter(isTaskJob).length;
    return counts;
}
